/**
 * The district facet pages' prose (SEO F9 follow-up).
 *
 * Before this, `/districts/:slug/{rent,buy}` rendered an `<h1>` mirroring its
 * own meta title, two links, and a client-hydrated listings grid — 400–580
 * RENDERED CHARACTERS, in every locale, across 36 pages that say almost the
 * same thing. Thin, and duplicate-ish with each other.
 *
 * ⚠ The measure is CHARACTERS PER LOCALE, never words. Ahrefs' "low word
 * count" flag whitespace-tokenizes, which scores Chinese at roughly a quarter:
 * every page it flagged on this site is zh-HK or zh-CN and none is `/en/`,
 * while `/forms` scores 49 "words" on 2,335 characters. A word-count target
 * would be permanently red on Chinese and permanently green on an empty
 * English page.
 *
 * EVERY VARYING PART IS DERIVED. Nothing here is written per district:
 *
 *   * the inventory count is `totalCount` — this district, this transaction
 *     mode, as of this request;
 *   * the region comes from the committed district vocabulary
 *     (`manifests/districts.yaml` → the generated closed tables), so it is the
 *     same fact the breadcrumb and the sitemap use;
 *   * the population comes from the committed 2021 Population Census dataset,
 *     keyed by `nameEn` — the same join key and the same generated lookup the
 *     estate pages' census section reads, carrying the same attribution.
 *
 * That is deliberate. Thirty-six hand-written district paragraphs (times three
 * locales) would be near-duplicate boilerplate that nobody updates, which is a
 * worse page than a short one. Derived copy differs page to page because the
 * FACTS differ, and it cannot go stale. For the same reason the advice
 * paragraph names nothing that changes — no prices, no rail lines, no
 * developments — only the things that actually move a Hong Kong asking price.
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { CENSUS_YEAR, districtCensus } from '@/shared/lib/estates/district-census_gen';
import { routes } from '@letsgethome/routes';
import { plaintextString, type Locale, type LocalizedText } from '@letsgethome/types/branded';
import {
  DISTRICTS,
  districtsInRegion,
  REGIONS,
  type DistrictSlug,
} from '@letsgethome/types/districts';

// The attribution targets, spelled exactly as the estates census section
// spells them (there is no SSOT lane for third-party URLs; the constants_gen
// lane carries first-party origins only).
const DATA_GOV_HK_URL = 'https://data.gov.hk';
const DATA_GOV_HK_TERMS_URL = 'https://data.gov.hk/en/terms-and-conditions';

type Props = {
  slug: DistrictSlug;
  /** The district's FORMAL localized name, as the loader projected it. */
  districtName: string;
  locale: Locale;
  mode: 'rent' | 'sale';
  /** Live inventory for this district in this mode. */
  totalCount: number;
};

/**
 * The lead paragraph: what this district is, and what is actually on the
 * market here right now.
 *
 * The zero case is its own sentence rather than "0 homes": a district with no
 * inventory serves `noindex, follow`, so the reader got here from a link and
 * is owed a plain statement and somewhere to go, not an empty grid under a
 * confident heading.
 */
export function DistrictFacetLede({ slug, districtName, locale, mode, totalCount }: Props) {
  const t = useTranslations('estates');
  const district = plaintextString(districtName);
  const region = plaintextString(REGIONS[DISTRICTS[slug].region].name[locale]);

  let about: LocalizedText;
  if (totalCount > 0) {
    about =
      mode === 'rent'
        ? t('districtFacet.about.rent', { district, region, count: totalCount })
        : t('districtFacet.about.buy', { district, region, count: totalCount });
  } else {
    about =
      mode === 'rent'
        ? t('districtFacet.aboutEmpty.rent', { district, region })
        : t('districtFacet.aboutEmpty.buy', { district, region });
  }

  return <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">{about}</p>;
}

/**
 * The closing section, below the grid: how to read prices in this district,
 * and who lives here.
 *
 * The census row is skipped rather than faked when the district is outside the
 * 18 official census districts — the same Rails-parity semantics
 * `CensusSection` uses. It cannot actually happen for a district facet (the
 * vocabulary is the census vocabulary), which is exactly why the branch stays:
 * the day the two diverge, the page drops a sentence instead of printing a
 * number for the wrong place.
 */
export function DistrictFacetAbout({
  slug,
  districtName,
  locale,
  mode,
}: Omit<Props, 'totalCount'>) {
  const t = useTranslations('estates');
  const district = plaintextString(districtName);
  const census = districtCensus(DISTRICTS[slug].nameEn);

  return (
    // `data-slot` so the browser gate can name this block without naming the
    // classes that lay it out. It is the half of the facet that a `canvas` row
    // was measured to endanger (#6915) and the reason the page is an `article`:
    // an SEO prose block is a document, not a pane.
    <section
      data-slot="district-facet-about"
      className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8"
    >
      <h2 className="text-2xl font-bold text-foreground">
        {t('districtFacet.aboutHeading', { district })}
      </h2>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
        {mode === 'rent'
          ? t('districtFacet.guidance.rent', { district })
          : t('districtFacet.guidance.buy', { district })}
      </p>
      {census && (
        <>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {t('districtFacet.population', {
              year: CENSUS_YEAR,
              population: plaintextString((census.males + census.females).toLocaleString(locale)),
              district,
            })}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('detail.census.dataSource', { year: CENSUS_YEAR })}
            {' · '}
            <a
              href={DATA_GOV_HK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {t('detail.census.attribution.dataGovHk')}
            </a>
            {' · '}
            <a
              href={DATA_GOV_HK_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {t('detail.census.attribution.terms')}
            </a>
          </p>
        </>
      )}
      <DistrictFacetNearby slug={slug} locale={locale} mode={mode} />
    </section>
  );
}

/**
 * The lateral mesh: every OTHER district in this district's region, linked to
 * the SAME facet — a rent page links to rent pages, a buy page to buy pages.
 *
 * The 36 facets had no edges between them at all. A visitor priced out of one
 * district had to climb back to `/districts` and start over, and a crawler had
 * exactly one way into each facet. The peer set is derived from the committed
 * district vocabulary (`districtsInRegion`), and each slug is already a
 * `DistrictSlug`, so a link here cannot address a district with no page.
 */
function DistrictFacetNearby({ slug, locale, mode }: Pick<Props, 'slug' | 'locale' | 'mode'>) {
  const t = useTranslations('estates');
  const region = DISTRICTS[slug].region;
  const peers = districtsInRegion(region).filter((d) => d.slug !== slug);
  if (peers.length === 0) {
    return null;
  }
  const regionName = plaintextString(REGIONS[region].name[locale]);

  return (
    <>
      <h3 className="mt-8 text-lg font-semibold text-foreground">
        {mode === 'rent'
          ? t('districtFacet.nearbyRent', { region: regionName })
          : t('districtFacet.nearbyBuy', { region: regionName })}
      </h3>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
        {peers.map((peer) => (
          <li key={peer.slug}>
            <Link
              href={
                mode === 'rent' ? routes.districts.rent(peer.slug) : routes.districts.buy(peer.slug)
              }
              prefetch="intent"
              className="text-success-text underline-offset-2 hover:underline"
            >
              {plaintextString(peer.shortName[locale])}
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
